import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Box, Button, Stack, Switch, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import CauseDetailPanel from "@/components/charity/CauseDetailPanel";
import { useCharityCauses } from "@/hooks/useCharityCauses";
import type { CauseSelection, CharityCause } from "@/interfaces/charity";

/**
 * "Select your cause beneficiary" — Stage 4 of Judd's campaign-builder prototype.
 *
 * Three states, as the prototype: the CHOSEN cause as a wide card with CHANGE CAUSE, a BROWSE grid
 * of polaroid cards, and the roll-over switch which replaces the whole section when turned on.
 *
 * Prices are deliberately absent. A client picks a cause, not a denomination — $5/$10/$25 is the
 * unit the closeout algorithm buys in, and surfacing it here would leak an implementation detail
 * into a values-based decision.
 *
 * Shares are not editable either: Judd's rule is "client selected beneficiary causes split evenly",
 * so the split is derived from the count and re-derived on every change.
 */

export const MAX_CAUSES = 6;

/**
 * Integer percentages summing to exactly 100, remainder spread a point at a time — the prototype's
 * evenShares. Integers because they are displayed AND because the stored fractions must sum to
 * precisely 1; [33.33, 33.33, 33.33] never will, and allocateBudget rejects it.
 */
export function evenShares(n: number): number[] {
  const base = Math.floor(100 / n);
  let rem = 100 - base * n;
  return Array.from({ length: n }, () => base + (rem-- > 0 ? 1 : 0));
}

/** Carried, not derived: with one cause selected both doors are on screen and `value` reads the
 *  same for each, so deriving it makes "+ Add causes" silently replace (GFG-240). */
type CausePickIntent = "add" | "replace";

/**
 * `replace` collapses the selection to exactly one cause: the set can never exceed MAX_CAUSES,
 * and nothing survives of the cause that left, shares included. `add` toggles — it drops a cause
 * that is already selected, which is how the "Remove" button is implemented.
 */
export function applyCausePick(
  current: CauseSelection[],
  pickedId: string,
  intent: CausePickIntent,
): CauseSelection[] {
  const ids = current.map((v) => v.causeProductId);

  if (intent === "add" && ids.length >= MAX_CAUSES && !ids.includes(pickedId)) return current;

  const next =
    intent === "replace"
      ? [pickedId]
      : ids.includes(pickedId)
        ? ids.filter((x) => x !== pickedId)
        : [...ids, pickedId];

  const pct = evenShares(next.length);
  return next.map((causeProductId, i) => ({ causeProductId, share: (pct[i] ?? 0) / 100 }));
}

const TAN = "var(--color-dust-ink)";

const ORG_SX = {
  fontSize: 11,
  letterSpacing: "1.1px",
  textTransform: "uppercase" as const,
  fontWeight: 700,
  color: TAN,
  lineHeight: 1.35,
};

/**
 * The cause photo.
 *
 * NO FRAME DRAWN HERE. The image is the product's PRIMARY Shopify image, which is authored with the
 * framing the cause is meant to be shown in. A tilted white frame used to be drawn around it, from
 * the prototype's polaroid treatment — which meant a cause whose artwork already carried a frame
 * showed two, at opposing angles, and one whose artwork carried none appeared framed when Shopify
 * intended it flat.
 *
 * No category icon badge either — the prototype does not draw one over the photo.
 */
function Polaroid({ cause, size }: { cause: CharityCause; size?: number }) {
  return (
    <Box
      component="img"
      src={cause.imageUrl ?? ""}
      alt=""
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
      }}
      sx={{
        display: "block",
        width: size ?? "100%",
        aspectRatio: "1",
        // Holds the row thumbnail at `size` while the photo is still loading: an unloaded img has
        // no intrinsic width, so the flex row would squeeze it and then jump on load.
        flexShrink: 0,
        objectFit: "cover",
        borderRadius: "2px",
      }}
    />
  );
}

export default function CauseSelector({
  value,
  onChange,
  rollover = false,
  onRolloverChange,
}: {
  value: CauseSelection[];
  onChange: (next: CauseSelection[]) => void;
  /** true = credits roll over instead of being donated at close. campaigns.donate_unredeemed. */
  rollover?: boolean;
  onRolloverChange?: (next: boolean) => void;
}) {
  const { data: causes = [], isLoading } = useCharityCauses();
  const [browsing, setBrowsing] = useState(false);
  const [pickIntent, setPickIntent] = useState<CausePickIntent>("add");
  /** Cards open the detail panel; selection happens from inside it, via "Add this cause". */
  const [detail, setDetail] = useState<CharityCause | null>(null);

  const byId = useMemo(() => new Map(causes.map((c) => [c.causeProductId, c])), [causes]);
  const selected = value
    .map((v) => byId.get(v.causeProductId))
    .filter((c): c is CharityCause => c != null);

  function browse(intent: CausePickIntent) {
    setPickIntent(intent);
    setBrowsing(true);
  }

  function leaveBrowse() {
    setPickIntent("add");
    setBrowsing(false);
  }

  function pick(id: string, intent: CausePickIntent) {
    onChange(applyCausePick(value, id, intent));
  }

  /**
   * Adding or removing from the detail panel closes it AND leaves the grid, landing back on the
   * list of chosen causes.
   *
   * The decision is made in the panel, so the grid has served its purpose — and the list is where
   * the consequence is visible: the new even split across every cause, and who the credits now go
   * to. Staying in the grid would hide the effect of the choice just made.
   */
  function toggleFromPanel(id: string) {
    pick(id, pickIntent);
    setDetail(null);
    leaveBrowse();
  }

  const pct = evenShares(Math.max(selected.length, 1));
  const orgs = selected
    .map((c) => c.organization)
    .join(", ")
    .replace(/, ([^,]*)$/, " and $1");

  return (
    <Box>
      <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 23 }}>
        {rollover ? "Credits will roll over" : "Select your cause beneficiary"}
      </Typography>
      <Box sx={{ height: "1px", bgcolor: "var(--color-card-border)", my: 2 }} />

      {/* Roll-over ON hides the cause area entirely, as the prototype does. The cause is still
          stored: roll-over defers the donation to the lot's own expiry, it never removes the need
          to have chosen one. */}
      {!rollover && (
        <>
          {isLoading && <Typography sx={{ fontSize: 13 }}>Loading causes…</Typography>}

          {!isLoading && !browsing && (
            <>
              {selected.length === 0 ? (
                <Button variant="outlined" onClick={() => browse("add")}>
                  Choose a cause
                </Button>
              ) : (
                <Stack spacing={1.2}>
                  {selected.map((c, i) => (
                    <Stack
                      key={c.causeProductId}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ bgcolor: "var(--color-clear-dust)", borderRadius: 2, p: 1.2, pr: 2.5 }}
                    >
                      <Polaroid cause={c} size={120} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={ORG_SX}>{c.organization}</Typography>
                        <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.4 }}>
                          {c.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13.5, color: "var(--color-graphite)", mt: 0.3 }}
                        >
                          {[c.cause, selected.length > 1 ? `${pct[i]}% of unused credits` : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                      <Button
                        onClick={() =>
                          selected.length === 1 ? browse("replace") : pick(c.causeProductId, "add")
                        }
                        sx={{ ...ORG_SX, fontSize: 12, whiteSpace: "nowrap" }}
                      >
                        {selected.length === 1 ? "Change cause" : "Remove"}
                      </Button>
                    </Stack>
                  ))}
                  {selected.length < MAX_CAUSES && (
                    <Button
                      variant="outlined"
                      onClick={() => browse("add")}
                      sx={{
                        ...ORG_SX,
                        fontSize: 12,
                        alignSelf: "flex-start",
                        px: 2.2,
                        py: 1.1,
                        borderColor: "var(--color-card-border)",
                        "&:hover": {
                          borderColor: "var(--color-dark-dust)",
                          bgcolor: "var(--color-clear-dust)",
                        },
                      }}
                    >
                      + Add causes
                    </Button>
                  )}
                </Stack>
              )}

              {selected.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
                  <FavoriteIcon sx={{ fontSize: 17, color: TAN, mt: "2px" }} />
                  <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                    Unused gift credits will be{" "}
                    {selected.length > 1 ? "split evenly between" : "sent to"}{" "}
                    <strong>{orgs}</strong>
                  </Typography>
                </Stack>
              )}
            </>
          )}

          {/* BROWSE — the polaroid grid. */}
          {!isLoading && browsing && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Button onClick={() => leaveBrowse()} sx={{ ...ORG_SX, fontSize: 13 }}>
                  ‹ Back
                </Button>
                <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
                  {pickIntent === "replace"
                    ? "The cause you pick replaces the one you have"
                    : `${value.length}/${MAX_CAUSES} selected`}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  // Two per row, fixed — NOT auto-fill. A `minmax` floor packs in more columns as
                  // the panel widens, which shrinks the photo on the widest screens (GFG-262).
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 2,
                  // Tied to the card height, which now tracks the panel width, so this has to hold
                  // two rows of a full-width photo rather than the old fixed 148px one.
                  maxHeight: 1200,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {causes.map((c) => {
                  // Nothing is marked as kept while a replace is pending: the next pick takes all.
                  const isOn =
                    pickIntent === "add" &&
                    value.some((v) => v.causeProductId === c.causeProductId);
                  return (
                    <Box
                      key={c.causeProductId}
                      onClick={() => setDetail(c)}
                      sx={{
                        position: "relative",
                        border: `1px solid ${isOn ? "var(--color-rodeo-black)" : "var(--color-card-border)"}`,
                        borderRadius: 2.5,
                        p: 1.5,
                        cursor: "pointer",
                        bgcolor: "#fff",
                        transition: "border-color .15s",
                        "&:hover": { borderColor: "var(--color-rodeo-dust)" },
                      }}
                    >
                      {/* Already chosen — so the grid shows the current set at a glance rather than
                          making the client remember what they picked. */}
                      {isOn && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "var(--color-rodeo-black)",
                            color: "#fff",
                            display: "grid",
                            placeItems: "center",
                            zIndex: 1,
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 17 }} />
                        </Box>
                      )}
                      <Box sx={{ mb: 1 }}>
                        <Polaroid cause={c} />
                      </Box>
                      <Typography sx={ORG_SX}>{c.organization}</Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.5, lineHeight: 1.3 }}>
                        {c.title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      )}

      {onRolloverChange && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            mt: 2.5,
            border: "1px solid var(--color-card-border)",
            borderRadius: 2,
            px: 2,
            py: 1.2,
            bgcolor: "var(--color-clear-dust)",
          }}
        >
          <AutorenewIcon sx={{ fontSize: 19, color: TAN }} />
          <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 700 }}>
            Roll over unused credits to use in my next gift campaign
          </Typography>
          <Switch checked={rollover} onChange={(e) => onRolloverChange(e.target.checked)} />
        </Stack>
      )}

      <CauseDetailPanel
        // Keyed by the cause so opening a different one REMOUNTS the panel. That resets its
        // thumbnail index for free; an effect watching the id does the same thing with a rule to
        // suppress and a render where the old photo is still on screen.
        key={detail?.causeProductId ?? "none"}
        cause={detail}
        // False while a replace is pending, so the panel's CTA stays "Add this cause" and its
        // toggle cannot read as "remove" and leave the campaign with no beneficiary.
        selected={
          pickIntent === "add" &&
          detail != null &&
          value.some((v) => v.causeProductId === detail.causeProductId)
        }
        onToggle={toggleFromPanel}
        onClose={() => setDetail(null)}
      />

      {rollover && (
        <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mt: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 19, color: TAN, mt: "1px" }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.55 }}>
            Unused gift credits from this campaign will{" "}
            <strong>return to your credit wallet</strong> when the campaign closes. These credits
            will be available to use on future campaigns for up to 12 months after their purchase
            date, after which they will be sent to benefit the cause(s) you select here.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
